import React, { useEffect, useState } from "react";
import "./StockScanner.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";

const toNumber = (value) => {
    const parsedValue = Number.parseFloat(
        String(value ?? "")
            .replace(/,/g, "")
            .replace("%", "")
    );

    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatNumber = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null) {
        return value ?? "--";
    }

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const SYMBOL_SERIES_SUFFIX_SET = new Set(["EQ", "BE", "BZ", "BL", "SM", "ST"]);

const normalizeSymbol = (value) => {
    const rawValue = String(value ?? "").trim().toUpperCase();

    if (!rawValue) {
        return "";
    }

    const exchangeTrimmedValue = rawValue.includes(":")
        ? rawValue.split(":").pop()?.trim() ?? ""
        : rawValue;
    const symbolParts = exchangeTrimmedValue.split("-");

    if (symbolParts.length > 1) {
        const lastPart = symbolParts[symbolParts.length - 1];

        if (SYMBOL_SERIES_SUFFIX_SET.has(lastPart)) {
            return symbolParts.slice(0, -1).join("-");
        }
    }

    return exchangeTrimmedValue;
};

const normalizeAtlasKey = (value) => String(value ?? "").trim().toUpperCase();

const ATLAS_DROV_ABOVE_BOTH_BUY = "ATLAS_DROV_ABOVE_BOTH_BUY";
const ATLAS_DROV_ABOVE_WROV_BUY = "ATLAS_DROV_ABOVE_WROV_BUY";
const ATLAS_DROV_BELOW_BOTH_SELL = "ATLAS_DROV_BELOW_BOTH_SELL";
const ATLAS_DROV_BELOW_WROV_SELL = "ATLAS_DROV_BELOW_WROV_SELL";
const ATLAS_BULL_BUY = "ATLAS_BULL_BUY";
const ATLAS_INTRADAY_BUY = "ATLAS_INTRADAY_BUY";
const MID_CROSS_BUY = "MID_CROSS_BUY";

const ATLAS_DROV_BUY_TAB = "ATLAS_DROV_BUY";
const ATLAS_DROV_SELL_TAB = "ATLAS_DROV_SELL";
const ATLAS_BULL_BUY_TAB = "ATLAS_BULL_BUY_TAB";
const ATLAS_INTRADAY_BUY_TAB = "ATLAS_INTRADAY_BUY_TAB";
const MID_CROSS_BUY_TAB = "MID_CROSS_BUY_TAB";

const ATLAS_DROV_BUY_KEYS = [ATLAS_DROV_ABOVE_BOTH_BUY, ATLAS_DROV_ABOVE_WROV_BUY];
const ATLAS_DROV_SELL_KEYS = [ATLAS_DROV_BELOW_BOTH_SELL, ATLAS_DROV_BELOW_WROV_SELL];

const ATLAS_SECTION_TITLE_MAP = {
    [ATLAS_DROV_ABOVE_BOTH_BUY]: "ATLAS DROV ABOVE BOTH BUY",
    [ATLAS_DROV_ABOVE_WROV_BUY]: "ATLAS DROV ABOVE WROV BUY",
    [ATLAS_DROV_BELOW_BOTH_SELL]: "ATLAS DROV BELOW BOTH SELL",
    [ATLAS_DROV_BELOW_WROV_SELL]: "ATLAS DROV BELOW WROV SELL",
    [ATLAS_BULL_BUY]: "ATLAS BULL BUY",
    [ATLAS_INTRADAY_BUY]: "INTRADAY BUY",
    [MID_CROSS_BUY]: "MID CROSS BUY",
};

const matchesSectorRange = (sectorPercent, status, sectorRange) => {
    if (sectorPercent == null) {
        return false;
    }

    if (status === "BUY") {
        if (sectorRange === "all") return sectorPercent > 0;
        if (sectorRange === "0.5_to_1") return sectorPercent >= 0.5 && sectorPercent <= 1;
        if (sectorRange === "1_to_2") return sectorPercent > 1 && sectorPercent <= 2;
        if (sectorRange === "2_plus") return sectorPercent >= 2;
        return false;
    }

    if (status === "SELL") {
        if (sectorRange === "all") return sectorPercent < 0;
        if (sectorRange === "0.5_to_1") return sectorPercent <= -0.5 && sectorPercent > -1;
        if (sectorRange === "1_to_2") return sectorPercent <= -1 && sectorPercent > -2;
        if (sectorRange === "2_plus") return sectorPercent <= -2;
        return false;
    }

    return false;
};

const createSectorDetailsMap = (sectorData = [], isSectorMatch = () => false) =>
    sectorData.reduce((accumulator, sector) => {
        const sectorPercent = toNumber(sector?.pChange);

        if (sectorPercent == null || !isSectorMatch(sectorPercent)) {
            return accumulator;
        }

        (sector?.sectorialIndicesStockModelList ?? []).forEach((stock) => {
            const symbol = normalizeSymbol(stock?.symbol);

            if (!symbol || !SECTOR_SELECTED_STOCK_SET.has(symbol) || accumulator.has(symbol)) {
                return;
            }

            accumulator.set(symbol, {
                sectorName: sector?.index ?? "--",
                sectorPercent,
            });
        });

        return accumulator;
    }, new Map());

const createLotSizeBySymbolMap = (stockDayRangeData = []) =>
    stockDayRangeData.reduce((accumulator, stock) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker
        );
        const lotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot;

        if (!symbol || lotSize == null || accumulator.has(symbol)) {
            return accumulator;
        }

        accumulator.set(symbol, lotSize);
        return accumulator;
    }, new Map());

const attachSectorDetailsToStocks = (
    stocks = [],
    sectorDetailsBySymbol = new Map(),
    requireSectorDetails = true
) =>
    stocks
        .map((stock) => {
            const sectorDetails = sectorDetailsBySymbol.get(normalizeSymbol(stock.symbol));

            if (!sectorDetails && requireSectorDetails) {
                return null;
            }

            return {
                ...stock,
                sectorName: sectorDetails?.sectorName ?? stock?.sectorName ?? null,
                sectorPercent: sectorDetails?.sectorPercent ?? stock?.sectorPercent ?? null,
            };
        })
        .filter(Boolean);

const getAtlasScannerRowsByKey = (atlasDataSource, key) => {
    const normalizedKey = normalizeAtlasKey(key);

    if (atlasDataSource instanceof Map) {
        if (Array.isArray(atlasDataSource.get(key))) {
            return atlasDataSource.get(key);
        }

        for (const [entryKey, entryValue] of atlasDataSource.entries()) {
            if (normalizeAtlasKey(entryKey) === normalizedKey) {
                return Array.isArray(entryValue) ? entryValue : [];
            }
        }

        return [];
    }

    if (atlasDataSource && typeof atlasDataSource === "object" && !Array.isArray(atlasDataSource)) {
        if (Array.isArray(atlasDataSource[key])) {
            return atlasDataSource[key];
        }

        const matchedObjectKey = Object.keys(atlasDataSource).find(
            (entryKey) => normalizeAtlasKey(entryKey) === normalizedKey
        );

        return matchedObjectKey && Array.isArray(atlasDataSource[matchedObjectKey])
            ? atlasDataSource[matchedObjectKey]
            : [];
    }

    return [];
};

const hasAtlasScannerEntries = (atlasDataSource) => {
    if (atlasDataSource instanceof Map) {
        return atlasDataSource.size > 0;
    }

    return Boolean(
        atlasDataSource &&
        typeof atlasDataSource === "object" &&
        !Array.isArray(atlasDataSource) &&
        Object.keys(atlasDataSource).length > 0
    );
};

const normalizeScannerStocks = (stockScannerDataList = [], lotSizeBySymbol = new Map()) =>
    stockScannerDataList.map((stock, index) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker ??
            "--"
        ) || "--";
        const fnoLotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot ??
            lotSizeBySymbol.get(symbol) ??
            "--";

        return {
            symbolCount: stock?.symbolCount ?? stock?.symbol_count ?? stock?.symbolCnt ?? stock?.count ?? "--",
            symbol,
            status: String(stock?.status ?? "").trim().toUpperCase(),
            changePercent: toNumber(stock?.["%_change"] ?? stock?.changePercent),
            createdDate: stock?.createdDate ?? "--",
            createdTime: stock?.createdTime ?? "--",
            volume: stock?.volume,
            triggerDate: stock?.triggerDate ?? "--",
            triggerTime: stock?.triggerTime ?? "--",
            ltp: stock?.ltp ?? stock?.close,
            fnoLotSize,
            __scannerKey: `${stock?.symbol ?? stock?.stockSymbol ?? "stock"}-${index}`,
        };
    });

    const SECTOR_SELECTED_STOCK_SET = new Set([
        "UNOMINDA", "BAJAJ-AUTO", "TIINDIA", "BOSCHLTD", "SONACOMS", "TVSMOTOR", "EICHERMOT", "HEROMOTOCO", "M&M", "BHARATFORG",
        "FORCEMOTOR", "PFC", "RECLTD", "BAJAJFINSV", "MFSL", "LICHSGFIN", "JIOFIN", "CHOLAIFIN", "SHRIRAMFIN", "MUTHOOTFIN",
        "BSE", "SBIN", "BAJAJFINANCE", "DABUR", "TATACONSUM", "HINDUNILVR", "GODREJCP", "UNITEDSPR", "NESTLEIND", "ITC",
        "VBL", "UBL", "BRITANNIA", "COFORGE", "PERSISTENT", "LTM", "INFY", "HCLTECH", "TECHMM", "MPHASIS", "TCS", "OFSS",
        "NATIONALUM", "HINDCOPPER", "HINDZINC", "ADANIENT", "JSWSTEEL", "JINDALSTEL", "HINDALCO", "APLAPOLLO", "TATASTEEL",
        "VEDL", "MANKIND", "LAURUSLABS", "SUNPHARMA", "TORNTPHARM", "AUROPHARMA", "DIVISLAB", "GLENMARK", "ALKEM", "BIOCON",
        "DRREDDY", "CIPLA", "LUPIN", "OBEROIRLTY", "PRESTIGE", "GODREJPROP", "LODHA", "DLF", "TITAN", "AMBER", "HAVELLS",
        "DIXON", "VOLTAS", "BLUESTARCO", "RELIANCE", "OIL", "ATGL", "ONGC", "BPCL", "PIDILITIND", "SRF", "PIIND", "CGPOWER",
        "NTPC", "TORNTPOWER", "JSWENERGY", "TATAPOWER", "SIEMENS", "ADANIGREEN", "ADANIENSOL", "ABB", "ADANIPOWER",
        "COALINDIA", "POWERINDIA", "HAL", "BEL", "BDL", "BHEL", "MAZDOCK", "COCHINSHIP", "DALBHARAT", "POLYCAB",
        "PNBHOUSING", "DMART", "360ONE", "ETERNAL", "ASIANPAINT", "LT", "HDFCBANK", "BANDHANBNK", "INDUSINDBK", "TECHM",
        "KOTAKBANK", "ICICIBANK", "AXISBANK"
    ]);

export default function StockScanner() {
    const { options, stockScannerData, stockSectorAnalysisData, sectorialIndicesData,
        atlasScannerData
     } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl
    );

    const [stockScannerDataList, setStockScannerDataList] = useState([]);
    const [sectorData, setSectorData] = useState([]);
    const [atlasData, setAtlasData] = useState({});
    const [activeTab, setActiveTab] = useState(ATLAS_INTRADAY_BUY_TAB);
    const [buySectorRange, setBuySectorRange] = useState("all");
    const [sellSectorRange, setSellSectorRange] = useState("all");

    useEffect(() => {
        console.log("Received stock scanner data:", stockScannerData);
        setStockScannerDataList(Array.isArray(stockScannerData) ? stockScannerData : []);
    }, [stockScannerData]);

    useEffect(() => {
        setSectorData(Array.isArray(sectorialIndicesData) ? sectorialIndicesData : []);

        if (Array.isArray(sectorialIndicesData) && sectorialIndicesData.length > 0) {
            console.log("Sector Data:", sectorialIndicesData);
        }
    }, [sectorialIndicesData]);

    useEffect(() => {
        setAtlasData(
            atlasScannerData && typeof atlasScannerData === "object" && !Array.isArray(atlasScannerData)
                ? atlasScannerData
                : {}
        );

        if (
            atlasScannerData &&
            typeof atlasScannerData === "object" &&
            !Array.isArray(atlasScannerData) &&
            Object.keys(atlasScannerData).length > 0
        ) {
            console.log("Atlas Scanner Data:", atlasScannerData);
        }
    }, [atlasScannerData]);

    const getChangeCellClassName = (changePercent) => {
        if (changePercent > 0) return "positive";
        if (changePercent < 0) return "negative";
        return "neutral";
    };

    const getTopAndBottomCHP = (data) => {
        if (!Array.isArray(data) || data.length < 2) return {};

        const sorted = [...data].sort((a, b) => a.chp - b.chp);

        return {
            firstLowest: sorted[0],
            secondLowest: sorted[1],
            firstHighest: sorted[sorted.length - 1],
            secondHighest: sorted[sorted.length - 2],
        };
    };

    const getScannerHeaderSymbol = (value) =>
        String(value ?? "")
            .split(":")[1]?.split("-")[0] ?? "--";

    const getChpColor = (value) => {
        const num = Number(value);
        if (Number.isNaN(num)) return "white";
        if (num > 0) return "#0dc80d";
        if (num < 0) return "rgb(228, 67, 136)";
        return "white";
    };

    const stockDayRangeList = Array.isArray(options?.stockDayRangeModelList)
        ? options.stockDayRangeModelList
        : [];
    const lotSizeBySymbol = createLotSizeBySymbolMap(stockDayRangeList);
    const scannerStocks = normalizeScannerStocks(stockScannerDataList, lotSizeBySymbol);
    const hasAtlasData = hasAtlasScannerEntries(atlasData);
    const buySectorDetailsBySymbol = createSectorDetailsMap(
        sectorData,
        (sectorPercent) => matchesSectorRange(sectorPercent, "BUY", buySectorRange)
    );
    const allSectorDetailsBySymbol = createSectorDetailsMap(sectorData, () => true);
    const sellSectorDetailsBySymbol = createSectorDetailsMap(
        sectorData,
        (sectorPercent) => matchesSectorRange(sectorPercent, "SELL", sellSectorRange)
    );
    const buyStockList = attachSectorDetailsToStocks(
        scannerStocks.filter((stock) => stock.status === "BUY"),
        buySectorDetailsBySymbol
    );
    const sellStockList = attachSectorDetailsToStocks(
        scannerStocks.filter((stock) => stock.status === "SELL"),
        sellSectorDetailsBySymbol
    );
    const atlasDrovBuySections = ATLAS_DROV_BUY_KEYS.map((key) => ({
        key,
        title: ATLAS_SECTION_TITLE_MAP[key],
        stocks: attachSectorDetailsToStocks(
            normalizeScannerStocks(getAtlasScannerRowsByKey(atlasData, key), lotSizeBySymbol),
            buySectorDetailsBySymbol
        ),
    }));
    const atlasDrovSellSections = ATLAS_DROV_SELL_KEYS.map((key) => ({
        key,
        title: ATLAS_SECTION_TITLE_MAP[key],
        stocks: attachSectorDetailsToStocks(
            normalizeScannerStocks(getAtlasScannerRowsByKey(atlasData, key), lotSizeBySymbol),
            sellSectorDetailsBySymbol
        ),
    }));
    const atlasBullBuyStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(getAtlasScannerRowsByKey(atlasData, ATLAS_BULL_BUY), lotSizeBySymbol),
        buySectorDetailsBySymbol
    );
    const atlasIntradayBuyStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(getAtlasScannerRowsByKey(atlasData, ATLAS_INTRADAY_BUY), lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const midCrossBuyStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(getAtlasScannerRowsByKey(atlasData, MID_CROSS_BUY), lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const atlasDrovBuySharedSymbolSet = (() => {
        const [firstSection, secondSection] = atlasDrovBuySections;

        if (!firstSection || !secondSection) {
            return new Set();
        }

        const secondSectionSymbolSet = new Set(
            secondSection.stocks.map((stock) => normalizeSymbol(stock.symbol)).filter(Boolean)
        );

        return new Set(
            firstSection.stocks
                .map((stock) => normalizeSymbol(stock.symbol))
                .filter((symbol) => symbol && secondSectionSymbolSet.has(symbol))
        );
    })();
    const atlasDrovSellSharedSymbolSet = (() => {
        const [firstSection, secondSection] = atlasDrovSellSections;

        if (!firstSection || !secondSection) {
            return new Set();
        }

        const secondSectionSymbolSet = new Set(
            secondSection.stocks.map((stock) => normalizeSymbol(stock.symbol)).filter(Boolean)
        );

        return new Set(
            firstSection.stocks
                .map((stock) => normalizeSymbol(stock.symbol))
                .filter((symbol) => symbol && secondSectionSymbolSet.has(symbol))
        );
    })();
    const headerData = scannerStocks[0] ?? {};
    const scannerHeaderStats = getTopAndBottomCHP(stockSectorAnalysisData);
    const topBottomHeaderData = [
        {
            label1: "1st HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstHighest?.chp ?? "--",
        },
        {
            label1: "2nd HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondHighest?.chp ?? "--",
        },
        {
            label1: "1st LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstLowest?.chp ?? "--",
        },
        {
            label1: "2nd LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondLowest?.chp ?? "--",
        },
    ];
    const isSellLikeTab = activeTab === "SELL" || activeTab === ATLAS_DROV_SELL_TAB;
    const sectorThresholdOptions = isSellLikeTab
        ? [
            { value: "all", label: "ALL" },
            { value: "0.5_to_1", label: "<= -0.5 and > -1.0" },
            { value: "1_to_2", label: "<= -1.0 and > -2.0" },
            { value: "2_plus", label: "<= -2.0" },
        ]
        : [
            { value: "all", label: "ALL" },
            { value: "0.5_to_1", label: ">= 0.5 and <= 1.0" },
            { value: "1_to_2", label: "> 1.0 and <= 2.0" },
            { value: "2_plus", label: ">= 2.0" },
        ];
    const activeSectorRange = isSellLikeTab ? sellSectorRange : buySectorRange;
    const atlasDrovBuyCount = atlasDrovBuySections.reduce((total, section) => total + section.stocks.length, 0);
    const atlasDrovSellCount = atlasDrovSellSections.reduce((total, section) => total + section.stocks.length, 0);

    const renderTableRows = (stocks, emptyMessage, showCount = true, highlightedSymbolSet = new Set()) => {
        if (stocks.length === 0) {
            return (
                <tr>
                    <td colSpan={showCount ? "10" : "9"} className="stock-scanner-empty-cell">
                        {emptyMessage}
                    </td>
                </tr>
            );
        }

        return stocks.map((stock) => {
            const changePercentValue = stock.changePercent ?? 0;
            const isPositive = changePercentValue >= 0;
            const normalizedSymbol = normalizeSymbol(stock.symbol);
            const isHighlightedSymbol = highlightedSymbolSet.has(normalizedSymbol);
            const sectorPercentValue = toNumber(stock.sectorPercent);

            return (
                <tr key={stock.__scannerKey}>
                    <td>{stock.triggerDate}</td>
                    <td>{stock.triggerTime}</td>
                    <td className={isHighlightedSymbol ? "symbol-highlight" : ""}>{stock.symbol}</td>
                    <td>{formatNumber(stock.ltp)}</td>
                    <td className={getChangeCellClassName(changePercentValue)}>
                        {isPositive ? "+" : ""}
                        {formatNumber(changePercentValue)}%
                    </td>
                    {showCount && <td>{stock.symbolCount}</td>}
                    <td>{stock.sectorName ?? "--"}</td>
                    <td className={sectorPercentValue == null ? "" : getChangeCellClassName(sectorPercentValue)}>
                        {sectorPercentValue == null
                            ? "--"
                            : `${sectorPercentValue >= 0 ? "+" : ""}${formatNumber(sectorPercentValue)}%`}
                    </td>
                    <td>{stock.fnoLotSize ?? "--"}</td>
                    <td>{formatNumber(stock.volume)}</td>
                </tr>
            );
        });
    };

    const waitingMessage = stockScannerDataList.length > 0
        ? "No matching stocks found."
        : "Waiting for live data...";
    const atlasWaitingMessage = hasAtlasData
        ? "No matching stocks found."
        : "Waiting for live atlas data...";

    const renderSectorFilterHeader = (enableSectorFilter = true) => {
        if (!enableSectorFilter) {
            return "SECTOR (%)";
        }

        return (
            <div className="scanner-table-filter-header">
                <span>SECTOR (%)</span>
                <select
                    className="scanner-table-filter-select"
                    value={activeSectorRange}
                    onChange={(event) => {
                        const nextRange = event.target.value;

                        if (isSellLikeTab) {
                            setSellSectorRange(nextRange);
                            return;
                        }

                        setBuySectorRange(nextRange);
                    }}
                >
                    {sectorThresholdOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const renderScannerTable = (
        stocks,
        emptyMessage,
        showCount = true,
        highlightedSymbolSet = new Set(),
        enableSectorFilter = true
    ) => (
        <div className="table-scroll-area">
            <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table scanner-table">
                <thead>
                    <tr>
                        <th>DATE</th>
                        <th>TIME</th>
                        <th>SYMBOL</th>
                        <th>LTP</th>
                        <th>CHANGE (%)</th>
                        {showCount && <th>COUNT</th>}
                        <th>SECTOR</th>
                        <th>{renderSectorFilterHeader(enableSectorFilter)}</th>
                        <th>FNO LOT SIZE</th>
                        <th>VOLUME</th>
                    </tr>
                </thead>
                <tbody>{renderTableRows(stocks, emptyMessage, showCount, highlightedSymbolSet)}</tbody>
            </table>
        </div>
    );

    const renderAtlasSections = (sections) => (
        <div className="scanner-section-list">
            {sections.map((section) => (
                <div key={section.key} className="scanner-section-block">
                    <div className="scanner-section-title">
                        {section.title} ({section.stocks.length})
                    </div>
                    {renderScannerTable(
                        section.stocks,
                        atlasWaitingMessage,
                        false,
                        activeTab === ATLAS_DROV_BUY_TAB
                            ? atlasDrovBuySharedSymbolSet
                            : activeTab === ATLAS_DROV_SELL_TAB
                                ? atlasDrovSellSharedSymbolSet
                                : new Set()
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <div className="stock-scanner">
            <div
                className="sticky-top-shell"
                style={{ position: "sticky", top: 0, zIndex: 1200 }}
            >
                <header className="header">
                    <nav className="nav-container">
                        <div className="header-flex">
                            <div className="left-nav">
                                <div className="logo-container">
                                    <img
                                        src="/assets/images/noptionwave-logo-1.png"
                                        className="header-logo"
                                        alt="logo"
                                    />
                                </div>

                                {topBottomHeaderData.map((item, idx) => {
                                    const color = getChpColor(item.value2);

                                    return (
                                        <div className="header-item" key={`${item.label1}-${idx}`}>
                                            <span className="header-line" style={{ color }}>
                                                {item.label1} : {item.value1}
                                            </span>
                                            <span className="header-line" style={{ color }}>
                                                {item.label2} : {item.value2}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="right-nav">
                                <span className="header-line" style={{ color: "white" }}>
                                    Date : {headerData.createdDate ?? "--"}
                                </span>
                                <span className="header-line" style={{ color: "white" }}>
                                    Time : {headerData.createdTime ?? "--"}
                                </span>
                            </div>
                        </div>
                    </nav>
                </header>

                <div className="tab-container">
                    <div className="tab">
                        <button
                            className={`tablinks sub-header-font ${activeTab === ATLAS_INTRADAY_BUY_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(ATLAS_INTRADAY_BUY_TAB)}
                        >
                            INTRADAY BUY ({atlasIntradayBuyStocks.length})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === MID_CROSS_BUY_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(MID_CROSS_BUY_TAB)}
                        >
                            MID CROSS BUY ({midCrossBuyStocks.length})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === ATLAS_DROV_BUY_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(ATLAS_DROV_BUY_TAB)}
                        >
                            ATLAS DROV BUY ({atlasDrovBuyCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === ATLAS_DROV_SELL_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(ATLAS_DROV_SELL_TAB)}
                        >
                            ATLAS DROV SELL ({atlasDrovSellCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === ATLAS_BULL_BUY_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(ATLAS_BULL_BUY_TAB)}
                        >
                            ATLAS BULL BUY ({atlasBullBuyStocks.length})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === "BUY" ? "active" : ""}`}
                            onClick={() => setActiveTab("BUY")}
                        >
                            BUY ({buyStockList.length})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === "SELL" ? "active" : ""}`}
                            onClick={() => setActiveTab("SELL")}
                        >
                            SELL ({sellStockList.length})
                        </button>
                    </div>
                </div>
            </div>

            {(activeTab === "BUY" || activeTab === "SELL" || activeTab === ATLAS_DROV_BUY_TAB || activeTab === ATLAS_DROV_SELL_TAB || activeTab === ATLAS_BULL_BUY_TAB || activeTab === ATLAS_INTRADAY_BUY_TAB || activeTab === MID_CROSS_BUY_TAB) && (
                <div className="table-container">
                    {activeTab === "BUY" && renderScannerTable(
                        buyStockList,
                        stockScannerDataList.length > 0 ? "No BUY stocks found." : waitingMessage
                    )}

                    {activeTab === "SELL" && renderScannerTable(
                        sellStockList,
                        stockScannerDataList.length > 0 ? "No SELL stocks found." : waitingMessage
                    )}

                    {activeTab === ATLAS_DROV_BUY_TAB && renderAtlasSections(atlasDrovBuySections)}

                    {activeTab === ATLAS_DROV_SELL_TAB && renderAtlasSections(atlasDrovSellSections)}

                    {activeTab === ATLAS_BULL_BUY_TAB && (
                        <div className="scanner-section-list">
                            <div className="scanner-section-block">
                                <div className="scanner-section-title">
                                    {ATLAS_SECTION_TITLE_MAP[ATLAS_BULL_BUY]} ({atlasBullBuyStocks.length})
                                </div>
                                {renderScannerTable(atlasBullBuyStocks, atlasWaitingMessage, false)}
                            </div>
                        </div>
                    )}

                    {activeTab === ATLAS_INTRADAY_BUY_TAB && (
                        <div className="scanner-section-list">
                            <div className="scanner-section-block">
                                <div className="scanner-section-title">
                                    {ATLAS_SECTION_TITLE_MAP[ATLAS_INTRADAY_BUY]} ({atlasIntradayBuyStocks.length})
                                </div>
                                {renderScannerTable(
                                    atlasIntradayBuyStocks,
                                    atlasWaitingMessage,
                                    false,
                                    new Set(),
                                    false
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === MID_CROSS_BUY_TAB && (
                        <div className="scanner-section-list">
                            <div className="scanner-section-block">
                                <div className="scanner-section-title">
                                    {ATLAS_SECTION_TITLE_MAP[MID_CROSS_BUY]} ({midCrossBuyStocks.length})
                                </div>
                                {renderScannerTable(
                                    midCrossBuyStocks,
                                    atlasWaitingMessage,
                                    false,
                                    new Set(),
                                    false
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="fixed-footer">
                <div className="footer-left">
                    <span>noptionwave © 2026</span>
                </div>
            </div>
        </div>
    );
}
